<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';
import Input from 'dashboard/components-next/input/Input.vue';

export default {
  components: {
    LoadingState,
    SettingsFieldSection,
    NextButton,
    SelectInput,
    Input,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      selectedAgentBotId: null,
      agentDisplayName: '',
      companyName: '',
    };
  },
  computed: {
    ...mapGetters({
      agentBots: 'agentBots/getBots',
      uiFlags: 'agentBots/getUIFlags',
    }),
    currentInboxId() {
      return this.inbox?.id || this.$route.params.inboxId;
    },
    activeAgentBot() {
      return this.$store.getters['agentBots/getActiveAgentBot'](
        this.currentInboxId
      );
    },
  },
  watch: {
    activeAgentBot() {
      this.selectedAgentBotId = this.activeAgentBot.id;
      this.loadInboxConfig();
    },
  },
  mounted() {
    this.fetchBotData();
  },

  methods: {
    fetchBotData() {
      this.$store.dispatch('agentBots/get');
      this.$store.dispatch('agentBots/fetchAgentBotInbox', this.currentInboxId);
    },
    loadInboxConfig() {
      const config = this.$store.getters['agentBots/getBotInboxConfig'](
        this.currentInboxId
      );
      this.agentDisplayName = config.agent_name || '';
      this.companyName = config.company_name || '';
    },
    async updateActiveAgentBot() {
      try {
        const botInboxConfig = this.selectedAgentBotId
          ? {
              agent_name: this.agentDisplayName,
              company_name: this.companyName,
            }
          : undefined;

        await this.$store.dispatch('agentBots/setAgentBotInbox', {
          inboxId: this.inbox.id,
          botId: this.selectedAgentBotId ? this.selectedAgentBotId : undefined,
          botInboxConfig,
        });

        useAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.ERROR_MESSAGE'));
      }
    },
    async disconnectBot() {
      try {
        await this.$store.dispatch('agentBots/disconnectBot', {
          inboxId: this.inbox.id,
        });
        useAlert(
          this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_SUCCESS_MESSAGE')
        );
      } catch (error) {
        useAlert(
          error?.message ||
            this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<template>
  <div class="mx-6 max-w-4xl">
    <LoadingState v-if="uiFlags.isFetching || uiFlags.isFetchingAgentBot" />
    <form v-else @submit.prevent="updateActiveAgentBot">
      <SettingsFieldSection
        :label="$t('AGENT_BOTS.BOT_CONFIGURATION.TITLE')"
        :help-text="$t('AGENT_BOTS.BOT_CONFIGURATION.DESC')"
        class="[&>div]:!items-start"
      >
        <SelectInput
          v-model="selectedAgentBotId"
          :placeholder="$t('AGENT_BOTS.BOT_CONFIGURATION.SELECT_PLACEHOLDER')"
          :options="agentBots.map(bot => ({ value: bot.id, label: bot.name }))"
        />
        <template #extra>
          <div
            v-if="selectedAgentBotId"
            class="grid grid-cols-1 lg:grid-cols-8 mt-4"
          >
            <div class="col-span-1 lg:col-span-2 invisible" />
            <div class="col-span-1 lg:col-span-6 mx-1">
              <div class="flex flex-col gap-2 mb-4">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ $t('AGENT_BOTS.BOT_CONFIGURATION.IDENTITY.LABEL') }}
                </span>
                <p class="text-xs text-n-slate-10">
                  {{ $t('AGENT_BOTS.BOT_CONFIGURATION.IDENTITY.DESCRIPTION') }}
                </p>
                <div class="grid grid-cols-2 gap-3">
                  <Input
                    id="bot-agent-display-name"
                    v-model="agentDisplayName"
                    :label="
                      $t(
                        'AGENT_BOTS.BOT_CONFIGURATION.IDENTITY.AGENT_NAME.LABEL'
                      )
                    "
                    :placeholder="
                      $t(
                        'AGENT_BOTS.BOT_CONFIGURATION.IDENTITY.AGENT_NAME.PLACEHOLDER'
                      )
                    "
                  />
                  <Input
                    id="bot-company-name"
                    v-model="companyName"
                    :label="
                      $t(
                        'AGENT_BOTS.BOT_CONFIGURATION.IDENTITY.COMPANY_NAME.LABEL'
                      )
                    "
                    :placeholder="
                      $t(
                        'AGENT_BOTS.BOT_CONFIGURATION.IDENTITY.COMPANY_NAME.PLACEHOLDER'
                      )
                    "
                  />
                </div>
              </div>
            </div>
          </div>
          <div class="grid grid-cols-1 lg:grid-cols-8 mt-3">
            <div class="col-span-1 lg:col-span-2 invisible" />
            <div class="col-span-1 lg:col-span-6 flex gap-2 mx-1">
              <NextButton
                type="submit"
                :label="$t('AGENT_BOTS.BOT_CONFIGURATION.SUBMIT')"
                :is-loading="uiFlags.isSettingAgentBot || uiFlags.isUpdating"
              />
              <NextButton
                type="button"
                :disabled="!selectedAgentBotId"
                :is-loading="uiFlags.isDisconnecting"
                faded
                ruby
                @click="disconnectBot"
              >
                {{ $t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECT') }}
              </NextButton>
            </div>
          </div>
        </template>
      </SettingsFieldSection>
    </form>
  </div>
</template>
